/* eslint-disable complexity */
import type { ComponentProps } from 'react';
import React, { useCallback, useEffect, useState } from 'react';
import { ProductEventDataList } from 'corgi-types';

import { Flex } from 'common/components/Flex';
import LoadMoreButton from 'common/components/LoadButton/LoadMoreButton';
import LoadPrevButton from 'common/components/LoadButton/LoadPrevButton';
import { useIsObserved } from 'common/hooks/intersection-observer';
import { useLocalization } from 'common/providers/LocaleProvider';
import { transformArticleTrackingData } from 'common/tracking/transformArticlesTrackingData';
import { getUrlWithQueryParams } from 'common/utils/url';
import { useProductsList } from '../hooks/use-products-list';
import { useSaveRecentSearch } from '../hooks/use-save-recent-search';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { useProductCardFocus } from '../hooks/useProductCardFocus';
import { getConstructorAttributes, getPrevAndNextPage } from '../utils';
import ProductCardList from './ProductCardList';
import ProductListEmpty from './ProductListEmpty';
import type { ProductListViewProps } from './types';

const LOAD_MORE_ROOT_MARGIN = '500px';

const ListViewContainer = (props: ComponentProps<typeof Flex>) => (
  <Flex pt={['sp_4', 'sp_16']} flexDirection="column" width={1} {...props} />
);

// eslint-disable-next-line max-lines-per-function
const ProductListView = ({
  browsedByValue,
  resultId,
  browsedBy,
  title,
  listViewRibbon,
  listViewTeaser,
  relatedSearchItems,
  filters,
  storeSearch,
  firstImage,
  articles: initialArticles = [],
  visibleProductsCount,
  pageSize = 30,
  isMarketplace,
  baseUrl,
  deferInitialLoad = false,
  ...props
}: ProductListViewProps) => {
  const { r } = useLocalization();
  const { focusIndex, setFocusToIndex, resetFocus, itemRefs } =
    useProductCardFocus<HTMLAnchorElement>();

  const {
    totalCount,
    articles,
    prevPageOffset,
    offset,
    page,
    isLoading,
    isSearchMode,
    hasFetchedDeferred,
    triggerDeferredFetch,
    fetchPrevProducts,
    fetchMoreProducts,
    fetchingArticlesCount,
    isFetchingPrev,
    skeletonRef,
  } = useProductsList({
    ...props,
    pageSize,
    deferInitialLoad,
    articles: initialArticles.map(article => transformArticleTrackingData(article)),
  });

  useSaveRecentSearch({
    storeSearch,
    query: props.query,
    firstImage,
  });

  const handleFetchMoreProducts = async () => {
    const prevLength = articles.length;
    if (fetchMoreProducts) {
      await fetchMoreProducts();
      setFocusToIndex(prevLength);
    }
  };

  const hasMoreProductsToLoad = offset < totalCount;
  const [loadMoreRef, isLoadMoreObserved] = useIsObserved({
    rootMargin: LOAD_MORE_ROOT_MARGIN,
    disconnectOnIntersection: false,
  });

  const [manuallyActivated, setManuallyActivated] = useState(!deferInitialLoad);

  const { shouldUseInfiniteScroll, handleFetchMoreWithCounter } = useInfiniteScroll({
    isLoadMoreObserved,
    hasMoreProductsToLoad,
    isLoading,
    offset,
    onFetchMore: handleFetchMoreProducts,
  });

  const handleManualLoadMore = useCallback(() => {
    if (!manuallyActivated) {
      setManuallyActivated(true);
      if (!hasFetchedDeferred) {
        triggerDeferredFetch();

        return;
      }
    }
    handleFetchMoreWithCounter();
  }, [manuallyActivated, hasFetchedDeferred, triggerDeferredFetch, handleFetchMoreWithCounter]);

  const { nextPage, prevPage } = getPrevAndNextPage(page);

  const [prevPageUrl, setPrevPageUrl] = useState('');
  const [nextPageUrl, setNextPageUrl] = useState('');

  useEffect(() => {
    setPrevPageUrl(getUrlWithQueryParams({ page: prevPage }, baseUrl));
    setNextPageUrl(getUrlWithQueryParams({ page: nextPage }, baseUrl));
  }, [prevPage, nextPage, baseUrl]);

  if (totalCount === 0 || articles.length === 0) {
    return (
      <ProductListEmpty
        browsedByValue={browsedByValue}
        resultId={resultId}
        browsedBy={browsedBy}
        filters={filters}
        isSearchMode={isSearchMode}
      />
    );
  }
  const hasPreviousProductsToLoad = prevPageOffset >= 0;

  const attributes = getConstructorAttributes({
    isSearchMode,
    totalCount,
    browsedByValue,
    resultId,
    browsedBy,
  });

  return (
    <ListViewContainer
      data-corgi-id="product-list-view"
      {...attributes}
      data-testid="list-view-container"
    >
      {hasPreviousProductsToLoad && (
        <LoadPrevButton
          isLoading={isLoading}
          isRounded
          {...(hasPreviousProductsToLoad
            ? {
                href: prevPageUrl,
                ...(!isLoading && { onClick: fetchPrevProducts }),
              }
            : {})}
        />
      )}
      <ProductCardList
        articles={articles}
        productEventDataListType={
          isSearchMode ? ProductEventDataList.SearchList : ProductEventDataList.List
        }
        listViewRibbon={listViewRibbon}
        listViewTeaser={listViewTeaser}
        relatedSearchItems={relatedSearchItems}
        skeletonItemsCount={fetchingArticlesCount}
        withSkeletonAbove={isFetchingPrev}
        pageSize={pageSize}
        page={page}
        visibleProductsCount={visibleProductsCount}
        isMarketplace={isMarketplace}
        skeletonRef={skeletonRef}
        focusIndex={focusIndex}
        resetFocus={resetFocus}
        itemRefs={itemRefs}
      />
      <div {...(shouldUseInfiniteScroll && manuallyActivated ? { ref: loadMoreRef } : {})}>
        <LoadMoreButton
          isLoading={isLoading}
          count={articles.length}
          totalCount={totalCount}
          disabled={!hasMoreProductsToLoad}
          ariaLabel={r.t('h24_load_more_products', { count: articles.length, totalCount })}
          isRounded
          my="sp_0"
          {...(hasMoreProductsToLoad
            ? {
                href: nextPageUrl,
                ...(!isLoading && { onClick: handleManualLoadMore }),
              }
            : {})}
        />
      </div>
    </ListViewContainer>
  );
};

export default ProductListView;
